import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import '../blog.css';
import Share from './Share';
import SienBanner from './SienBanner';
import remarkSermonSupport from '../utils/remarkSermonSupport';
import { copySermonBody } from '../utils/sermonCopy';
import {
  DEFAULT_IMAGE,
  extractMarkdownMetadata,
  fetchStaticMarkdown,
  toAbsoluteImageUrl,
} from '../utils/markdownContent';

const AUTHOR_MAPPINGS = [
  {
    key: '[anna]',
    markup: `<div class="author-inline">
      <span class="iconpos">
        <img src='/static/images/anna.jpg' alt="斎藤アンナ" class="author-inline-icon" />
      </span>
      <span class="author">By 斎藤アンナ</span>
    </div>`,
  },
  {
    key: '[claude]',
    markup: `<div class="author-inline">
      <span class="iconpos">
        <img src='/static/images/claude.png' alt="Claude" class="author-inline-icon" />
      </span>
      <span class="author">By Claude</span>
    </div>`,
  },
  {
    key: '[sugano]',
    markup: `<div class="author-inline">
      <span class="iconpos">
        <img src='/static/images/sugano.jpg' alt="菅野契（管理人）" class="author-inline-icon" />
      </span>
      <span class="author">By 菅野契（管理人）</span>
    </div>`,
  },
];

function resolveFirstImage(markdown, fallbackImage) {
  const imageMatch = markdown.match(/!\[.*?\]\((.*?)\)/);
  if (!imageMatch || !imageMatch[1]) {
    return toAbsoluteImageUrl(fallbackImage);
  }
  return toAbsoluteImageUrl(imageMatch[1]);
}

function applyAuthorMarkup(text) {
  let updatedText = text;

  AUTHOR_MAPPINGS.forEach(({ key, markup }) => {
    if (updatedText.includes(key)) {
      updatedText = updatedText.replace(new RegExp(`\\${key}`, 'g'), markup);
    }
  });

  return updatedText;
}

function extractTitleFromMarkdown(markdown) {
  const lines = markdown.split('\n');
  const firstTitleLine = lines.find((line) => line.startsWith('#'));
  if (!firstTitleLine) {
    return '';
  }
  return firstTitleLine.replace(/^#\s*/, '').trim();
}

function MarkDown({
  path,
  preview,
  type,
  articleType,
  articleId,
  fallbackPath,
  suppressExtras = false,
}) {
  const [markdown, setMarkdown] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [firstImage, setFirstImage] = useState(DEFAULT_IMAGE);
  const [shareUrl, setShareUrl] = useState('https://www.asmrchurch.com');
  const [isAdmin, setIsAdmin] = useState(false);
  const bodyRef = useRef(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [isCopying, setIsCopying] = useState(false);

  useEffect(() => {
    if (!copyStatus) return;
    const timeout = setTimeout(() => setCopyStatus(''), 3000);
    return () => clearTimeout(timeout);
  }, [copyStatus]);

  useEffect(() => {
    let isMounted = true;
    setMarkdown('');
    setCopyStatus('');

    const loadContent = async () => {
      const staticPath = path || fallbackPath || (articleType && articleId ? `/${articleType}/${articleId}` : '');
      if (!staticPath) {
        return;
      }

      try {
        const text = await fetchStaticMarkdown(staticPath);
        if (!isMounted) {
          return;
        }

        const enriched = applyAuthorMarkup(text);
        const metadata = extractMarkdownMetadata(text);
        setMarkdown(enriched);
        setTitle(metadata.title || extractTitleFromMarkdown(text));
        if (articleType === 'journal' && articleId) {
          try {
            const listResponse = await fetch('/static/markdown/journal/list.json');
            if (listResponse.ok) {
              const listData = await listResponse.json();
              const matched = Array.isArray(listData.articles)
                ? listData.articles.find((entry) => String(entry.id) === String(articleId))
                : null;
              setCategory(matched && matched.category ? String(matched.category) : '');
            } else {
              setCategory('');
            }
          } catch (listError) {
            setCategory('');
          }
        } else {
          setCategory('');
        }
        setFirstImage(resolveFirstImage(text, metadata.rawImage));
        setShareUrl(`https://www.asmrchurch.com${staticPath}`);
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setMarkdown('');
        }
      }
    };

    loadContent();

    return () => {
      isMounted = false;
    };
  }, [path, articleType, articleId, fallbackPath]);

  useEffect(() => {
    let active = true;
    const checkSession = async () => {
      try {
        const response = await fetch('/api/admin/session', { credentials: 'include' });
        if (!active) {
          return;
        }
        const data = await response.json();
        setIsAdmin(Boolean(data?.authenticated));
      } catch (error) {
        if (active) {
          setIsAdmin(false);
        }
      }
    };
    checkSession();
    return () => {
      active = false;
    };
  }, []);

  const previewContent = preview
    ? markdown.split('\n').slice(0, 20).join('\n')
    : markdown;

  const resolvedType = articleType || type;
  const shareLabel = resolvedType === 'journal' ? '聖書新聞' : 'ASMRキリスト教会';

  const handleCopy = async () => {
    setIsCopying(true);
    try {
      await copySermonBody(bodyRef.current);
      setCopyStatus('本文をコピーしました');
    } catch (error) {
      setCopyStatus('コピーできませんでした。もう一度お試しください。');
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <div>
      <div className={`${type === 'manga' ? 'manga' : 'blog-section'}`}>
        {!suppressExtras && isAdmin && articleType && articleId && (
          <div className="admin-inline-edit">
            <a
              href={`/admin?type=${articleType}&id=${articleId}`}
              className="admin-edit-link"
            >
              編集する
            </a>
          </div>
        )}
        {!preview && !suppressExtras && articleType === 'journal' && category && (
          <div className="journal-category-badge">
            <span className="journal-category-badge-text">{category}</span>
          </div>
        )}
        {!preview && !suppressExtras && (
          <div className="markdown-banner">
            <a
              href="https://youtube.com/@asmrchurch"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/static/images/ba.gif"
                alt="ASMRキリスト教会YouTubeチャンネル"
              />
            </a>
          </div>
        )}
        {resolvedType === 'sermon' && !preview && !suppressExtras && (
          <div className="sermon-copy-actions">
            <button
              type="button"
              onClick={handleCopy}
              disabled={!markdown || isCopying}
              aria-label="本文をコピー"
              title={copyStatus || '本文をコピー'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {copyStatus === '本文をコピーしました' ? (
                  <path d="m5 12 4 4L19 6" />
                ) : (
                  <>
                    <rect x="8" y="8" width="12" height="13" rx="2" />
                    <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
                  </>
                )}
              </svg>
            </button>
            <span role="status" className={copyStatus && copyStatus !== '本文をコピーしました' ? 'sermon-copy-error' : 'sermon-copy-status'}>{copyStatus}</span>
          </div>
        )}
        <div ref={bodyRef}>
        <ReactMarkdown
          children={previewContent}
          remarkPlugins={resolvedType === 'sermon' && !preview && !suppressExtras
            ? [remarkGfm, remarkSermonSupport]
            : [remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          skipHtml={false}
          components={{
            div: ({ node, ...props }) => props['data-sermon-support'] === 'true'
              ? <SienBanner className="sermon-sien-after-sources" />
              : <div {...props} />,
            img: ({ node, ...props }) => <img {...props} />,
            a: ({ node, href, children, className, ...props }) => {
              if (href === 'https://youtube.com/@asmrchurch' || href === 'http://youtube.com/@asmrchurch') {
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" {...props} className="youtube-thumbnail-link">
                    <img
                      src="/static/images/thumbnail.png"
                      alt="YouTube Channel"
                      className="youtube-thumbnail"
                    />
                  </a>
                );
              }
              return <a href={href} className={className} {...props}>{children}</a>;
            },
          }}
        />
        </div>
      </div>

      {!preview && !suppressExtras && (
        <Share
          title={`【${shareLabel}】${title} @asmrchurch #聖書 #ASMR #キリスト教`}
          url={shareUrl}
          text={markdown}
          image={firstImage}
        />
      )}
    </div>
  );
}

export default MarkDown;
